"use client";
import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { FileCheck, CheckCircle2, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./allSection.css";
import axios from "axios";
import API_URL from "../../../src/Config";
import DashboardFilters from "../../Component/dashboard-filters";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../Component/ui/chart";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="newdashboard-tooltip">
        <p className="newdashboard-tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function StatutoryApprovalsSection() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({});
  const [charts, setCharts] = useState({});
  const [departments, setDepartments] = useState([]);

  // ✅ Filters
  const [filters, setFilters] = useState({
    department_id: "",
    date_preset: "all",
    showCustomDate: false,
    start_date: "",
    end_date: "",
  });

  // ✅ Fetch Statutory Approvals data
  const fetchStatutoryApprovals = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");

      const params = new URLSearchParams();
      if (currentFilters.date_preset)
        params.append("filter_option", currentFilters.date_preset);
      if (currentFilters.department_id)
        params.append("department_id", currentFilters.department_id);
      if (currentFilters.start_date)
        params.append("start_date", currentFilters.start_date);
      if (currentFilters.end_date)
        params.append("end_date", currentFilters.end_date);

      const response = await axios.get(
        `${API_URL}/api/kpi/statutory-approvals?${params.toString()}`,
        { headers: { Authorization: authKey } }
      );

      if (response.status === 200) {
        const data = response.data;
        setSummary(data.summary || {});
        setCharts(data.charts || {});
        setDepartments(data.departments_allowed || []);
      }
    } catch (error) {
      console.error("Error fetching Statutory Approvals data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Apply filters
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchStatutoryApprovals(newFilters);
  };

  // ✅ Reset filters
  const handleResetFilters = () => {
    const reset = {
      department_id: "",
      date_preset: "all",
      showCustomDate: false,
      start_date: "",
      end_date: "",
    };
    setFilters(reset);
    fetchStatutoryApprovals(reset);
  };

  // ✅ Initial fetch
  useEffect(() => {
    fetchStatutoryApprovals();
  }, []);

  // Card click handler
  const handleCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);
    
    const filterOptionValue = filters.date_preset ? filters.date_preset : "All";
    params.append("filter_option", filterOptionValue);
    
    if (filters.department_id && filters.department_id.trim() !== "") {
      params.append("department_id", filters.department_id);
    }
    
    if (filters.start_date && filters.end_date && 
        filters.start_date.trim() !== "" && filters.end_date.trim() !== "") {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }

    console.log("Navigating to Statutory Approvals List with params:", params.toString());
    
    window.open(`/statutory-approvals-list?${params.toString()}`, "_blank");
    // navigate(`/statutory-approvals-list?${params.toString()}`);
  };

  const totalApprovals = summary.total_available_approvals || {};
  const validApprovals = summary.valid_approvals || {};
  const dueApprovals = summary.due_in_next_month || {};

  const approvalStatusByType =
    charts.approval_status_by_type && charts.approval_status_by_type.length > 0
      ? charts.approval_status_by_type
      : [];

  const approvalStatusDistribution = charts.approval_status_distribution || {};
  const approvalStatusDistributionData = [
    {
      status: "Valid",
      valid: approvalStatusDistribution.valid || 0,
      due: approvalStatusDistribution.due_next_month || 0,
      expired: approvalStatusDistribution.expired || 0,
    },
  ];

  const validApprovalsTrend = charts.valid_approvals_trend || [];

  return (
    <div className="newdashboard-sa-section">
      {loading ? (
        <div className="loader-container">
          <div className="loader"></div>
          <div>Loading...</div>
        </div>
      ) : (
        <>
          {/* Header + Filters */}
          <div className="newdashboard-sa-header">
            <div>
              <h2 className="newdashboard-section-title">
                Statutory Approvals
              </h2>
              <p className="newdashboard-sa-subtitle">
                Track compliance and approval status
              </p>
            </div>

            {/* ✅ Controlled Filters with department list */}
            <DashboardFilters
              filters={filters}
              onFilterChange={setFilters}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              departments={departments} // pass department options to filter
            />
          </div>

          {/* KPI Cards - Now Clickable */}
          <div className="newdashboard-sa-kpi-grid">
            <div 
              className="newdashboard-sa-card newdashboard-card-clickable"
              onClick={() => handleCardClick("all_approvals")}
              style={{ cursor: "pointer" }}
            >
              <div className="newdashboard-sa-card-header">
                <h3 className="newdashboard-sa-card-title">
                  Total Available Approvals
                </h3>
                <FileCheck className="newdashboard-sa-card-icon" />
              </div>
              <div className="newdashboard-sa-card-content">
                <div className="newdashboard-sa-kpi-value newdashboard-sa-kpi-valid">
                  {totalApprovals.count ?? 0}
                </div>
                <p className="newdashboard-sa-kpi-note">
                  {totalApprovals.label || "All approval types"}
                </p>
              </div>
            </div>

            <div 
              className="newdashboard-sa-card newdashboard-card-clickable"
              onClick={() => handleCardClick("valid_approvals")}
              style={{ cursor: "pointer" }}
            >
              <div className="newdashboard-sa-card-header">
                <h3 className="newdashboard-sa-card-title">Valid Approvals</h3>
                <CheckCircle2 className="newdashboard-sa-card-icon" />
              </div>
              <div className="newdashboard-sa-card-content">
                <div className="newdashboard-sa-kpi-value newdashboard-sa-kpi-available">
                  {validApprovals.count ?? 0}
                </div>
                <p className="newdashboard-sa-kpi-note">
                  {validApprovals.compliance_rate ?? 0}% compliance rate
                </p>
              </div>
            </div>

            <div 
              className="newdashboard-sa-card newdashboard-card-clickable"
              onClick={() => handleCardClick("due_next_month")}
              style={{ cursor: "pointer" }}
            >
              <div className="newdashboard-sa-card-header">
                <h3 className="newdashboard-sa-card-title">
                  Due in Next Month
                </h3>
                <Clock className="newdashboard-sa-card-icon" />
              </div>
              <div className="newdashboard-sa-card-content">
                <div className="newdashboard-sa-kpi-value newdashboard-sa-kpi-balance">
                  {dueApprovals.count ?? 0}
                </div>
                <p className="newdashboard-sa-kpi-note">
                  {dueApprovals.label || "Renewal required"}
                </p>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="newdashboard-sa-charts">
            {/* Approval Status Distribution */}
            <div className="newdashboard-sa-chart-card">
              <div className="newdashboard-sa-chart-header">
                <h3 className="newdashboard-sa-chart-title">
                  Approval Status Distribution
                </h3>
                <p className="newdashboard-sa-chart-description">
                  Valid vs Due vs Expired
                </p>
              </div>
              <div className="newdashboard-sa-chart-content">
                {approvalStatusDistributionData.every(
                  (d) => d.valid === 0 && d.due === 0 && d.expired === 0
                ) ? (
                  <p className="no-data-text">No data found.</p>
                ) : (
                  <div className="newdashboard-chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={approvalStatusDistributionData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="status" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar dataKey="valid" fill="hsl(142, 76%, 36%)" />
                        <Bar dataKey="due" fill="hsl(38, 92%, 50%)" />
                        <Bar dataKey="expired" fill="hsl(0, 84%, 60%)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* Valid Approvals Trend */}
            <div className="newdashboard-sa-chart-card">
              <div className="newdashboard-sa-chart-header">
                <h3 className="newdashboard-sa-chart-title">
                  Valid Approvals Trend
                </h3>
                <p className="newdashboard-sa-chart-description">
                  Valid approvals over time
                </p>
              </div>
              <div className="newdashboard-sa-chart-content">
                {validApprovalsTrend.length === 0 ? (
                  <p className="no-data-text">No data found.</p>
                ) : (
                  <div className="newdashboard-chart-container">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={validApprovalsTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="valid_approvals"
                          stroke="hsl(142, 76%, 36%)"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>

            {/* Approval Type Breakdown */}
            <div className="newdashboard-sa-department">
              <div className="newdashboard-sa-department-header">
                <h3 className="newdashboard-sa-department-title">
                  Approval Status by Type
                </h3>
                <p className="newdashboard-sa-department-description">
                  Breakdown by approval category
                </p>
              </div>
              <div className="newdashboard-sa-department-content">
                {approvalStatusByType.length === 0 ? (
                  <p className="no-data-text">No data found.</p>
                ) : (
                  <ChartContainer
                    config={{
                      valid: { label: "Valid", color: "hsl(142, 76%, 36%)" },
                      due: {
                        label: "Due Next Month",
                        color: "hsl(38, 92%, 50%)",
                      },
                      expired: { label: "Expired", color: "hsl(0, 84%, 60%)" },
                    }}
                    className="newdashboard-sa-chart"
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={approvalStatusByType}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" />
                        <YAxis />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <Legend />
                        <Bar
                          dataKey="valid"
                          fill="var(--color-valid)"
                          name="Valid"
                        />
                        <Bar
                          dataKey="due_next_month"
                          fill="var(--color-due)"
                          name="Due Next Month"
                        />
                        <Bar
                          dataKey="expired"
                          fill="var(--color-expired)"
                          name="Expired"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}